import React, { useCallback, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';

import api from '../utils/api';
import Loader from '../lib/components/Loader';

const clampWeek = week => Math.min(Math.max(Number(week) || 1, 1), 17);

// Pin the logged-in manager's own matchup to the top of its group.
const pinMine = (matchups, myManager) => {
    if (!myManager) {
        return matchups;
    }
    const mine = matchups.filter(m => m[myManager]);
    return [...mine, ...matchups.filter(m => !m[myManager])];
};

const HeadToHead = ({ matchup, isMine }) => {
    const [a, b] = Object.keys(matchup);
    return (
        <div className={`home-matchup ${isMine ? 'home-matchup-mine' : ''}`}>
            <NavLink to={`/league/${a}`} className="home-matchup-team">
                <span className="text-truncate">{matchup[a].name}</span>
                <strong>{matchup[a].teamPoints}</strong>
            </NavLink>
            <span className="home-matchup-vs">vs</span>
            <NavLink to={`/league/${b}`} className="home-matchup-team home-matchup-team-right">
                <strong>{matchup[b].teamPoints}</strong>
                <span className="text-truncate">{matchup[b].name}</span>
            </NavLink>
        </div>
    );
};

// Battle Royale / Sacko groups rank every team in one list; the top half wins.
const RankedList = ({ matchup, myManager }) => {
    const managers = Object.keys(matchup);
    const lastWinningPlace = managers.length / 2;
    return (
        <div>
            {managers.map((manager, i) => {
                const wins = i + 1 <= lastWinningPlace;
                return (
                    <NavLink
                        key={manager}
                        to={`/league/${manager}`}
                        className={`home-ranked-row ${wins ? 'home-ranked-win' : 'home-ranked-lose'} ${
                            manager === myManager ? 'home-matchup-mine' : ''
                        }`}
                    >
                        <span className="home-ranked-place">{i + 1}</span>
                        <span className="text-truncate flex-fill">{matchup[manager].name}</span>
                        <strong>{matchup[manager].teamPoints}</strong>
                    </NavLink>
                );
            })}
        </div>
    );
};

/**
 * This week's matchups, from the same API as the Scoreboard (head-to-head,
 * Battle Royale and playoff/Sacko weeks). A logged-in manager's own matchup is
 * pinned first and highlighted; admins have no team, so they see the plain list.
 * Loads once; the refresh button re-fetches.
 */
const MatchupsCard = ({ userData }) => {
    const [week, updateWeek] = useState(null);
    const [groups, updateGroups] = useState([]);
    const [loading, updateLoading] = useState(true);
    const [isError, updateIsError] = useState(false);

    const myManager = userData && userData.permissions && userData.permissions !== 'admin'
        ? userData.permissions
        : null;

    const load = useCallback(() => {
        updateLoading(true);
        api.get('/api/v2/matchups/currentWeek')
            .then(({ data }) => clampWeek(data.currentWeek))
            .catch(() => 1)
            .then(currentWeek => {
                updateWeek(currentWeek);
                return api.get(`/api/v2/matchups?week=${currentWeek}`);
            })
            .then(({ data }) => {
                updateGroups(data);
                updateIsError(false);
            })
            .catch(() => updateIsError(true))
            .finally(() => updateLoading(false));
    }, []);

    useEffect(load, [load]);

    return (
        <div className="home-card">
            <div className="d-flex justify-content-between align-items-baseline mb-2">
                <h5 className="mb-0">Matchups{week ? ` - Week ${week}` : ''}</h5>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={load}
                    disabled={loading}
                    aria-label="Refresh matchups"
                >
                    <i className="bi bi-arrow-clockwise"></i>
                </button>
            </div>
            {loading ? <Loader /> : isError ? (
                <div className="alert alert-danger mb-2">Error loading matchups!</div>
            ) : groups.length === 0 ? (
                <div className="text-muted">No matchups found.</div>
            ) : groups.map(group => (
                <div key={group.matchupTitle} className="mb-2">
                    {groups.length > 1 && <div className="text-muted small mb-1">{group.matchupTitle}</div>}
                    {pinMine(group.matchups, myManager).map((matchup, i) => {
                        const size = Object.keys(matchup).length;
                        return size === 2
                            ? <HeadToHead key={i} matchup={matchup} isMine={!!matchup[myManager]} />
                            : <RankedList key={i} matchup={matchup} myManager={myManager} />;
                    })}
                </div>
            ))}
            <NavLink to="/scoreboard" className="small">
                Full scoreboard <i className="bi bi-chevron-right"></i>
            </NavLink>
        </div>
    );
};

export default MatchupsCard;
